import { PlatformAddOns } from '../interfaces/platform';
import { CallsPlatformModel } from './calls-platform.model';
import { ConversationIntelligencePlatformModel } from './conversation-intelligence-platform.model';
import { FormsPlatformModel } from './forms-platform.model';
import { LeadCenterPlatformModel } from './lead-center-platform.model';
import { ConvertAssistPlatformModel } from './convert-assist-platform.model';
import { VoiceAssistPlatformModel } from './voice-assist-platform.model';

const PLATFORM_MODELS = {
  calls: CallsPlatformModel,
  forms: FormsPlatformModel,
  conversation_intelligence: ConversationIntelligencePlatformModel,
  lead_center: LeadCenterPlatformModel,
  convert_assist: ConvertAssistPlatformModel,
  voice_assist: VoiceAssistPlatformModel,
} as const;

type PlatformName = keyof typeof PLATFORM_MODELS;

/** Representing an agency's billing subscription,
 * no matter what state the agency is in.
 */
// billing subscription model
export class BillingSubscriptionModel {
  public platformKeys: PlatformName[];
  public platforms = this.initPlatforms();
  public featureTrials = this.initPlatforms();

  public addOns: PlatformAddOns[];

  public chargedThrough: string; // date
  public annualRenewalDate: string;
  public version?: number;

  /**
   * Whether there are annual plans
   */
  public annualPlans = false;

  public monthlyCost: {
    platforms: number;
    add_ons: number;
    total: number;
  };

  public annualCost: {
    platforms: number;
    add_ons: number;
    total: number;
  };

  constructor(fromData) {
    this.addOns = fromData?.add_ons ? fromData?.add_ons : [];
    this.chargedThrough = fromData?.charged_through;
    this.annualPlans = fromData?.has_annual_plans;
    this.monthlyCost = fromData?.cost?.monthly || null;
    this.annualCost = fromData?.cost?.annual || null;
    this.annualRenewalDate = fromData?.annual_renewal_date;
    this.fillPlatforms(fromData.platforms);
    this.fillFeatureTrials(fromData.feature_trials);
    this.platformKeys = Object.keys(this.platforms).filter(
      (platform) => !!this.platforms[platform]
    ) as any;
    this.version = fromData?.version;
  }

  /**
   * Returns shortest expiration in days from the subscribed platforms
   * for providing generic notices across multiple platforms.
   */
  public get expiresIn(): number {
    // platformKeys represents an array of non-null platforms
    const daysInTrial = this.platformKeys
      .map((key) => this.platforms[key].daysInTrial)
      .filter((days) => days != null);

    // Math.min() returns Infinity if it isn't passed any arguments
    // which can happen if no platforms are in trial.
    const minDaysInTrial = Math.min(...daysInTrial);
    const expiresIn = Math.max(
      isFinite(minDaysInTrial) ? minDaysInTrial : 0,
      0
    );

    // We actually give customers a 15 day trial to account for
    // late in the day signups. An example is a user signs up for
    // a trial at 11pm. We still want to give them a full 14 days
    // to trial our products. But since all our marketing materials
    // say 14 day trial, we want to show 14 days in the UI even if
    // technically they have 15 days remaining.
    if (expiresIn === 15) {
      return 14;
    } else {
      return expiresIn;
    }
  }

  private initPlatforms() {
    return {
      calls: null as CallsPlatformModel,
      forms: null as FormsPlatformModel,
      conversation_intelligence: null as ConversationIntelligencePlatformModel,
      lead_center: null as LeadCenterPlatformModel,
      convert_assist: null as ConvertAssistPlatformModel,
      voice_assist: null as VoiceAssistPlatformModel,
    };
  }

  private fillPlatforms(platforms: Record<PlatformName, any>) {
    for (const [name, platform] of Object.entries(platforms)) {
      const model = PLATFORM_MODELS[name];
      if (platform && model) {
        this.platforms[name] = new model(platform.current);
      }
    }
  }

  private fillFeatureTrials(platforms: Record<PlatformName, any>) {
    for (const [name, platform] of Object.entries(platforms)) {
      const model = PLATFORM_MODELS[name];
      if (platform && model) {
        this.featureTrials[name] = new model(platform);
      }
    }
  }
}
